import { useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSearchOpcs4Codes } from "./useSearchOpcs4Codes";
import type { OpcsVersion } from "./opcsVersion";
import { getOpcsVersionLabel } from "./opcsVersion";

const Opcs4CodesList = () => {
  const [search, setSearch] = useState("");
  const [version, setVersion] = useState<OpcsVersion>(11);
  const debouncedSearch = useDebounce(search, 300);
  const { codes, isLoading, error, fetchNextPage, hasNextPage } = useSearchOpcs4Codes(
    debouncedSearch,
    undefined,
    version,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Button variant={version === 10 ? "default" : "outline"} onClick={() => setVersion(10)} size="sm">
          {getOpcsVersionLabel(10)}
        </Button>
        <Button variant={version === 11 ? "default" : "outline"} onClick={() => setVersion(11)} size="sm">
          {getOpcsVersionLabel(11)}
        </Button>
      </div>
      <div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search OPCS-4 codes or descriptions..."
          aria-label="Search OPCS-4 codes"
          className="w-full max-w-md"
        />
      </div>
      {isLoading && <div>Loading...</div>}
      {error && <div className="text-destructive">Error loading codes. Please try again.</div>}
      {!isLoading && codes && codes.length === 0 && <div>No results found.</div>}
      {!isLoading && codes && codes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border">
            <thead>
              <tr className="bg-muted">
                <th className="px-2 py-1 text-left">Code</th>
                <th className="px-2 py-1 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.code} className="border-t">
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{code.code}</td>
                  <td className="px-2 py-1">{code.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasNextPage && (
        <div>
          <Button onClick={fetchNextPage} disabled={isLoading} size="sm">
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

export default Opcs4CodesList;
